import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, Sparkles, TrendingDown, TrendingUp } from 'lucide-react';
import {
  type CategoryScore,
  type SnapshotEntityType,
  type SnapshotReport,
  ModuleRequiredError,
  downloadSnapshot,
  generateSnapshot,
  listSnapshots,
} from '@/api/snapshot';
import { Button } from '@/components/ui/Button';

function scoreColor(score: number): string {
  if (score >= 70) return 'text-emerald-600';
  if (score >= 40) return 'text-amber-600';
  return 'text-red-600';
}

function barColor(score: number): string {
  if (score >= 70) return 'bg-emerald-500';
  if (score >= 40) return 'bg-amber-500';
  return 'bg-red-500';
}

/**
 * SnapshotPanel renders the AI needs-assessment snapshot for an account /
 * opportunity / lead object page (epic 126.4): overall score, per-category
 * breakdown with evidence, trend vs the previous snapshot, and a (module-gated)
 * generate action.
 */
export function SnapshotPanel({
  entityType,
  entityId,
}: {
  entityType: SnapshotEntityType;
  entityId: string;
}) {
  const { t, i18n } = useTranslation();
  const [latest, setLatest] = useState<SnapshotReport | null>(null);
  const [previous, setPrevious] = useState<SnapshotReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [moduleRequired, setModuleRequired] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    listSnapshots(entityType, entityId)
      .then((history) => {
        setLatest(history[0] ?? null);
        setPrevious(history[1] ?? null);
      })
      .catch(() => {
        // Reads are ungated; a failure just leaves the empty state.
        setLatest(null);
        setPrevious(null);
      })
      .finally(() => setLoading(false));
  }, [entityType, entityId]);

  useEffect(() => {
    load();
  }, [load]);

  async function onGenerate() {
    setGenerating(true);
    setModuleRequired(false);
    try {
      await generateSnapshot(entityType, entityId);
      load();
    } catch (err) {
      if (err instanceof ModuleRequiredError) {
        setModuleRequired(true);
      }
    } finally {
      setGenerating(false);
    }
  }

  async function onExport() {
    setExporting(true);
    try {
      await downloadSnapshot(entityType, entityId, i18n.language);
    } finally {
      setExporting(false);
    }
  }

  const overallDelta = latest && previous ? latest.overallScore - previous.overallScore : null;

  return (
    <div className="rounded-lg border bg-card p-6" data-testid="snapshot-panel">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          <Sparkles className="h-4 w-4 text-[var(--color-lop-teal)]" />
          {t('snapshot.title')}
        </h2>
        <div className="flex gap-2">
          {latest && (
            <Button size="sm" variant="outline" onClick={onExport} disabled={exporting} data-testid="snapshot-export">
              <Download className="mr-1 h-4 w-4" />
              {exporting ? t('snapshot.exporting') : t('snapshot.export')}
            </Button>
          )}
          <Button size="sm" onClick={onGenerate} disabled={generating} data-testid="snapshot-generate">
            {generating
              ? t('snapshot.generating')
              : latest
                ? t('snapshot.regenerate')
                : t('snapshot.generate')}
          </Button>
        </div>
      </div>

      {moduleRequired && (
        <p className="mb-3 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-700" role="alert">
          {t('snapshot.moduleRequired')}
        </p>
      )}

      {loading ? (
        <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
      ) : !latest ? (
        <p className="text-sm text-muted-foreground">{t('snapshot.empty')}</p>
      ) : (
        <div className="space-y-4">
          <div className="flex items-baseline gap-3">
            <span className={`text-4xl font-bold ${scoreColor(latest.overallScore)}`} data-testid="snapshot-overall">
              {Math.round(latest.overallScore)}
            </span>
            <span className="text-sm text-muted-foreground">/ 100</span>
            {overallDelta != null && Math.abs(overallDelta) >= 0.5 && (
              <span
                className={`flex items-center gap-1 text-xs font-medium ${overallDelta >= 0 ? 'text-emerald-600' : 'text-red-600'}`}
              >
                {overallDelta >= 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                {overallDelta >= 0 ? '+' : ''}
                {overallDelta.toFixed(0)}
              </span>
            )}
          </div>

          <ul className="space-y-3">
            {latest.categories.map((c) => (
              <CategoryRow key={c.categoryKey} cat={c} prev={previous?.categories.find((p) => p.categoryKey === c.categoryKey)} />
            ))}
          </ul>

          <p className="text-xs text-muted-foreground">
            {t('snapshot.generatedAt', {
              date: new Date(latest.generatedAt).toLocaleString(),
            })}{' '}
            · {latest.modelId} v{latest.modelVersion}
          </p>
        </div>
      )}
    </div>
  );
}

function CategoryRow({ cat, prev }: { cat: CategoryScore; prev?: CategoryScore }) {
  const { t } = useTranslation();
  const delta = prev && prev.available && cat.available ? cat.subScore - prev.subScore : null;
  return (
    <li data-testid="snapshot-category">
      <div className="mb-1 flex items-center justify-between text-sm">
        <span className="font-medium">{t(cat.labelI18nKey)}</span>
        {cat.available ? (
          <span className="flex items-center gap-1.5">
            <span className={scoreColor(cat.subScore)}>{Math.round(cat.subScore)}</span>
            {delta != null && Math.abs(delta) >= 0.5 && (
              <span className={`text-xs ${delta >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                ({delta >= 0 ? '+' : ''}
                {delta.toFixed(0)})
              </span>
            )}
          </span>
        ) : (
          <span className="text-xs italic text-muted-foreground">{t('snapshot.unavailable')}</span>
        )}
      </div>
      {cat.available && (
        <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
          <div className={`h-full ${barColor(cat.subScore)}`} style={{ width: `${Math.min(100, cat.subScore)}%` }} />
        </div>
      )}
      {cat.evidence && <p className="mt-1 text-xs text-muted-foreground">{cat.evidence}</p>}
    </li>
  );
}
